import Swiper from 'swiper';
import { Navigation, A11y } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';

const WIDE_BREAKPOINT = 960;
const TABLET_BREAKPOINT = 768;
const WIDE_SLIDES_PER_VIEW = 5;
const TABLET_SLIDES_PER_VIEW = 3;
const MOBILE_SLIDES_PER_VIEW = 1;

function shouldEnableCarousel(cardCount: number, viewportWidth: number): boolean {
    if (cardCount <= 1) {
        return false;
    }

    if (viewportWidth >= WIDE_BREAKPOINT) {
        return cardCount > WIDE_SLIDES_PER_VIEW;
    }

    return true;
}

function getSlidesPerView(viewportWidth: number): number {
    if (viewportWidth >= WIDE_BREAKPOINT) {
        return WIDE_SLIDES_PER_VIEW;
    }

    if (viewportWidth >= TABLET_BREAKPOINT) {
        return TABLET_SLIDES_PER_VIEW;
    }

    return MOBILE_SLIDES_PER_VIEW;
}

function getSpaceBetween(viewportWidth: number): number {
    return viewportWidth >= WIDE_BREAKPOINT ? 12 : 10;
}

function getCarouselOptions(
    carousel: HTMLElement,
    viewportWidth: number
): SwiperOptions {
    const slidesPerView = getSlidesPerView(viewportWidth);
    const carouselWrap = carousel.closest('.banner__cards-carousel');

    return {
        modules: [Navigation, A11y],
        slidesPerView,
        slidesPerGroup: 1,
        spaceBetween: getSpaceBetween(viewportWidth),
        speed: 500,
        loop: true,
        watchOverflow: true,
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        a11y: {
            enabled: true,
        },
        navigation: {
            prevEl: carouselWrap?.querySelector<HTMLElement>('[data-banner-cards-prev]') ?? null,
            nextEl: carouselWrap?.querySelector<HTMLElement>('[data-banner-cards-next]') ?? null,
        },
    };
}

function setupBannerCardsCarousel(carousel: HTMLElement): void {
    const cardCount = Number.parseInt(carousel.dataset.bannerCardCount || '0', 10);
    let swiper: Swiper | null = null;
    let resizeTimer: number | undefined;

    const updateCarousel = (): void => {
        const viewportWidth = window.innerWidth;
        const enableCarousel = shouldEnableCarousel(cardCount, viewportWidth);

        if (swiper) {
            swiper.destroy(true, true);
            swiper = null;
        }

        carousel.classList.remove('banner__cards--carousel-active');

        if (!enableCarousel) {
            return;
        }

        carousel.classList.add('banner__cards--carousel-active');
        swiper = new Swiper(carousel, getCarouselOptions(carousel, viewportWidth));
    };

    const handleResize = (): void => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(updateCarousel, 200);
    };

    updateCarousel();
    window.addEventListener('resize', handleResize);
}

export function setupBannerCards(): void {
    document.querySelectorAll<HTMLElement>('[data-banner-cards]').forEach(setupBannerCardsCarousel);
}

setupBannerCards();
